import { isArray } from 'lodash'

import { DQLHTTPResponse } from '../types'

/**
 * A utility function to assert that a response is a DQLHTTPResponse. This enforces
 * the structure of the response object, ensuring all of the *required* fields are present
 * and of the correct type.
 */
export const isDQLHTTPResponse = (
  response: unknown,
): response is DQLHTTPResponse => {
  return (
    typeof response === 'object' &&
    response !== null &&
    typeof (response as DQLHTTPResponse).queryType === 'string' &&
    typeof (response as DQLHTTPResponse).items === 'object' &&
    Array.isArray((response as DQLHTTPResponse).items) &&
    typeof (response as DQLHTTPResponse).error === 'object' &&
    typeof (response as DQLHTTPResponse).warnings === 'object' &&
    Array.isArray((response as DQLHTTPResponse).warnings)
  )
}

/**
 * A utility function to extract the collection name from a DQL query. This handles various
 * DQL command types including:
 * - SELECT queries (e.g., "SELECT * FROM collection [WHERE|LIMIT|OFFSET]...")
 * - UPDATE queries (e.g., "UPDATE collection SET...")
 * - INSERT queries (e.g., "INSERT INTO collection [DOCUMENTS]...")
 *
 * The function validates the command structure around the collection name:
 * - For UPDATE: Must start with UPDATE and have SET after collection
 * - For SELECT: Must have FROM before collection, optionally followed by WHERE or LIMIT
 * - For INSERT: Must have INTO before collection, optionally followed by DOCUMENTS
 *
 * Collection names can be wrapped in backticks for names containing special characters
 * or spaces (e.g., "SELECT * FROM `my collection`"). The function will preserve the backticks
 * in the returned collection name if they were present in the original query.
 *
 * This function preserves the original casing of the collection name while performing
 * case-insensitive matching for the SQL commands. It also handles the special case where
 * "collection" is used as a collection name (e.g., "SELECT * FROM collection collection").
 *
 * @param query - The DQL query string to parse
 * @returns The collection name if found and valid, or null if:
 *   - No valid command structure is found
 *   - The query contains 'from collection' (reserved for future use)
 */
export const findCollectionName = (query: string) => {
  const originalQuery = query
  const lowerQuery = query.toLowerCase()

  // Helper function to extract name with original casing from a match
  const extractNameWithOriginalCasing = (
    match: RegExpMatchArray,
    groupIndex = 1,
  ) => {
    if (!match || match.index === undefined) return null

    // Calculate the exact position of the collection name in the original string:
    // 1. match.index gives us the start position of the entire match in the lowercase string
    // 2. match[0].indexOf(match[groupIndex]) gives us the offset of the group (collection name) within the match
    // 3. Using these together, we find the exact character position where the collection name starts
    const matchStart = match.index + match[0].indexOf(match[groupIndex])
    const matchLength = match[groupIndex].length

    // Extract the collection name with its original casing from the original query string
    const name = originalQuery.substring(matchStart, matchStart + matchLength)

    // Handle backtick-wrapped names
    if (name.startsWith('`')) {
      const fullNameMatch = originalQuery.substring(matchStart).match(/`[^`]+`/)
      const fullName = fullNameMatch?.[0]
      return fullName || name
    }

    return name
  }

  // First check for UPDATE queries since they need special handling
  const updateMatch = lowerQuery.match(/^\s*update\s+([^\s]+)\s+set\b/)
  if (updateMatch) return extractNameWithOriginalCasing(updateMatch)

  // Specifically handle two cases of "collection" as the collection name
  // TODO: (Tom) I'm still not convinced this is the best way to handle this, but it works for now.
  // There's a conversation to be had around reserved keywords, imo.
  if (lowerQuery.includes('collection collection')) {
    // Rather than using extractNameWithOriginalCasing, manually extract the original
    // casing of the 2nd collection name in the original query
    const match = originalQuery.match(/collection collection/i)
    if (match && match.index !== undefined) {
      const matchStart = match.index + match[0].lastIndexOf('collection')
      const matchLength = 'collection'.length
      return originalQuery.substring(matchStart, matchStart + matchLength)
    }
  }

  // Handle SELECT and INSERT queries
  const selectMatch = lowerQuery.match(
    /\bfrom\s+(?:collection\s+)?([^\s]+)(?:\s+(?:where|limit|offset)\b|\s*$)/,
  )
  const insertMatch = lowerQuery.match(
    /\binto\s+(?:collection\s+)?([^\s]+)(?:\s+documents\b|\s*$)/,
  )

  const match = selectMatch || insertMatch
  if (match) return extractNameWithOriginalCasing(match)

  return null
}

/**
 * Validates if a string is a non-empty JSON object or array of objects
 * @param jsonToValidate The JSON string to validate
 * @returns true if the string is valid JSON containing either:
 * - A non-empty object with at least one property
 * - A non-empty array of objects, where each object has at least one property
 * @returns false for empty objects, empty arrays, primitives, or invalid JSON
 */
export const validateJsonObjectContent = (jsonToValidate: string): boolean => {
  jsonToValidate = jsonToValidate.replace(/\n/g, '').trim()

  try {
    const parsed = JSON.parse(jsonToValidate)

    // Handle case where the parsed result is a string that might contain JSON
    if (typeof parsed === 'string') {
      const trimmed = parsed.trim()
      if (
        (trimmed.startsWith('{') && trimmed.endsWith('}')) ||
        (trimmed.startsWith('[') && trimmed.endsWith(']'))
      ) {
        try {
          return validateJsonObjectContent(trimmed)
        } catch {
          return false
        }
      }

      return false
    }

    if (parsed === null || parsed === undefined) return false

    // Check a normal JSON object has keys
    if (typeof parsed === 'object' && !isArray(parsed)) {
      return Object.keys(parsed).length > 0
    }

    // Handle arrays of objects
    if (isArray(parsed)) {
      if (parsed.length === 0) return false

      if (parsed.every((item) => typeof item === 'object' && item !== null)) {
        return parsed.every((item) => Object.keys(item).length > 0)
      }
    }

    // Primitives (string, number, boolean)
    return false
  } catch {
    return false
  }
}

type BuildQueryForCollectionOptions = {
  limit?: number
  offset?: number
}

/**
 * Generates a SQL query for selecting records from a collection with an optional limit and offset.
 * Handles wrapping collection names in backticks when needed (for names with special characters).
 *
 * @param collection - The name of the collection to query
 * @param options - Optional limit and offset on the number of records to return
 * @returns A properly formatted SQL query string
 */
export const buildQueryForCollection = (
  collection: string,
  options?: BuildQueryForCollectionOptions,
): string => {
  const hasLimit = options?.limit && options.limit > 0
  const limitClause = hasLimit ? ` LIMIT ${options.limit}` : ''

  const offsetClause =
    options?.offset && options.offset > 0 ? ` OFFSET ${options.offset}` : ''

  return `SELECT * FROM \`${collection}\`${limitClause}${offsetClause}`
}

/**
 * Ensures apostrophes are escaped in a string by converting them to a special character.
 *
 * @param str - The string to escape apostrophes in
 * @returns The escaped string with apostrophes converted to a special character
 */
export const escapeApostrophes = (str: string): string => {
  return str.replace(/'/g, "''")
}

/**
 * Escapes line endings in a string by converting all line ending types to spaces.
 * Handles Windows (\r\n), Unix (\n), Classic Mac (\r), and reversed (\n\r) formats.
 * Removes trailing spaces if present.
 *
 * @param str - The string to escape line endings in
 * @returns The escaped string with all line endings converted to spaces, trailing spaces removed
 */
export const escapeLineEndings = (str: string): string => {
  const normalized = str
    .replace(/\r\n/g, '\n')
    .replace(/\n\r/g, '\n')
    .replace(/\r/g, '\n')

  const result = normalized.split('\n').join(' ')
  return result.endsWith(' ') ? result.slice(0, -1) : result
}

/**
 * Escapes all special characters, including apostrophes, carriage returns, and new lines.
 *
 * @param str - The string to escape all special characters in
 * @returns The escaped string with all special characters converted to a special character
 */
export const escapeAll = (str: string): string => {
  return escapeApostrophes(escapeLineEndings(str))
}

/**
 * Escapes apostrophes in the keys and values of a JSON object. This function:
 * - Recursively processes nested objects and arrays
 * - Preserves non-string values (numbers, booleans, null)
 * - Handles arrays of mixed types, including deeply nested arrays
 * - Escapes apostrophes in both keys and string values
 *
 * Example:
 * ```typescript
 * const input = {
 *   "user's name": "John's data",
 *   "nested": {
 *     "array": [
 *       "item's value",
 *       { "key's": "value's" },
 *       ["nested array's item"]
 *     ]
 *   }
 * }
 * ```
 *
 * @param json - The JSON object to escape apostrophes in
 * @returns A new object with escaped apostrophes in keys and string values
 * @throws {Error} If the input contains circular references
 */
export const escapeApostrophesInJson = <T extends Record<string, unknown>>(
  json: T,
  path: unknown[] = [],
): T => {
  const processValue = (value: unknown, currentPath: unknown[]): unknown => {
    if (typeof value === 'string') {
      return escapeAll(value)
    }

    if (Array.isArray(value)) {
      return value.map((item) => processValue(item, currentPath))
    }

    if (value && typeof value === 'object') {
      // Check for circular references before processing the object
      if (currentPath.includes(value)) {
        throw new Error('Circular reference detected in JSON object')
      }

      return escapeApostrophesInJson(value as Record<string, unknown>, [
        ...currentPath,
        value,
      ])
    }

    return value
  }

  return Object.entries(json).reduce(
    (result, [key, value]) => ({
      ...result,
      [escapeAll(key)]: processValue(value, path),
    }),
    {} as T,
  )
}

/**
 * Determines if a DQL query is a data-modifying operation that should be executed in the background.
 *
 * Background execution is used for operations that modify data (INSERT, UPDATE, DELETE, TOMBSTONE)
 * to preserve the user's editor context. Read-only queries (SELECT, EXPLAIN, etc.) are executed
 * normally and update the editor results.
 *
 * This function checks for data-modifying commands, handling:
 * - Direct commands (INSERT, UPDATE, DELETE, TOMBSTONE)
 * - CTEs (WITH clauses) that end with data-modifying commands (not supported in DQL yet)
 * - Comments and whitespace before commands
 *
 * @param query - The DQL query string to check
 * @returns true if the query modifies data and should run in background, false otherwise
 */
export const isDataModifyingQuery = (query: string): boolean => {
  if (!query) return false

  // Remove leading/trailing whitespace and normalize to lowercase
  const normalizedQuery = query.trim().toLowerCase()

  // Remove SQL comments to get to the actual query
  // Remove single-line comments (-- comment)
  let cleanQuery = normalizedQuery.replace(/--[^\n]*/g, '')
  // Remove multi-line comments (/* comment */)
  cleanQuery = cleanQuery.replace(/\/\*[\s\S]*?\*\//g, '')
  // Trim again after removing comments
  cleanQuery = cleanQuery.trim()

  // Data-modifying commands that should run in background
  const modifyingCommands = ['insert', 'update', 'delete', 'tombstone']

  // Check if query starts with a modifying command
  for (const command of modifyingCommands) {
    if (cleanQuery.startsWith(command)) {
      return true
    }
  }

  // Handle CTEs - look for the main command after WITH clause(s)
  if (cleanQuery.startsWith('with')) {
    // Find the main query after the CTE definition(s)
    // Look for ) followed by a DQL command
    const cteEndPattern = /\)\s*(select|insert|update|delete|tombstone)\s/gi
    const matches = [...cleanQuery.matchAll(cteEndPattern)]

    if (matches.length > 0) {
      // Get the last match (the main query after CTEs)
      const lastMatch = matches[matches.length - 1]
      const mainCommand = lastMatch[1].toLowerCase()
      return modifyingCommands.includes(mainCommand)
    }
  }

  // Default to false - treat as read-only query
  return false
}
